import { getDesignerSummaryKeyedRows, isBayLikePayload } from './visualDesignerSummary';
import { htmlToPlainText } from './richTextDescription';

export type SurveyDeltaReviewGroupId =
  | 'quote_details'
  | 'windows_doors'
  | 'dimensions_design'
  | 'specification';

export type SurveyDeltaReviewRow = {
  id: string;
  group: SurveyDeltaReviewGroupId;
  item: string | null;
  label: string;
  original: string | null;
  final: string | null;
};

export type SurveyDeltaReviewSection = {
  id: SurveyDeltaReviewGroupId;
  title: string;
  rows: SurveyDeltaReviewRow[];
};

const GROUP_TITLES: Record<SurveyDeltaReviewGroupId, string> = {
  quote_details: 'Quote details',
  windows_doors: 'Windows & doors',
  dimensions_design: 'Dimensions & design',
  specification: 'Specification',
};

const HEADER_LABELS: Record<string, { label: string; group: SurveyDeltaReviewGroupId }> = {
  title: { label: 'Title', group: 'quote_details' },
  description: { label: 'Description', group: 'quote_details' },
  deposit_amount: { label: 'Deposit', group: 'quote_details' },
  deposit_percentage: { label: 'Deposit percentage', group: 'quote_details' },
  has_deposit: { label: 'Deposit required', group: 'quote_details' },
  project_days: { label: 'Project duration', group: 'quote_details' },
  project_hours: { label: 'Project hours', group: 'quote_details' },
  vat_enabled: { label: 'VAT', group: 'quote_details' },
  vat_pct: { label: 'VAT rate', group: 'quote_details' },
  admin_pct: { label: 'Administration fee', group: 'quote_details' },
  admin_cap: { label: 'Administration cap', group: 'quote_details' },
  contingency_pct: { label: 'Contingency', group: 'quote_details' },
  profit_pct: { label: 'Profit', group: 'quote_details' },
  payment_structure_applicable: { label: 'Staged payments', group: 'quote_details' },
  payment_structure_pattern: { label: 'Payment schedule', group: 'quote_details' },
};

function asNumber(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function money(value: unknown): string {
  const n = asNumber(value);
  if (n == null) return '';
  const wholePounds = Number.isInteger(n);
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: wholePounds ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(n);
}

function percent(value: unknown): string {
  const n = asNumber(value);
  if (n == null) return '';
  const display = n > 0 && n <= 1 ? n * 100 : n;
  return `${Number(display.toFixed(2)).toString().replace(/\.00$/, '')}%`;
}

function yesNo(value: unknown): string {
  if (value == null || value === '') return '';
  return value === true || value === 'true' || value === 1 || value === '1' ? 'Yes' : 'No';
}

function daysLabel(value: unknown): string {
  const n = asNumber(value);
  if (n == null) return '';
  return n === 1 ? '1 day' : `${n} days`;
}

function hoursLabel(value: unknown): string {
  const n = asNumber(value);
  if (n == null) return '';
  return n === 1 ? '1 hour' : `${n} hours`;
}

function text(value: unknown): string {
  if (value == null) return '';
  return String(value).trim();
}

function formatHeaderValue(key: string, header: Record<string, unknown>): string {
  if (key === 'deposit_amount') {
    if (header.has_deposit === false) return 'None';
    return money(header.deposit_amount);
  }
  if (key === 'deposit_percentage') return percent(header.deposit_percentage);
  if (key === 'has_deposit') return yesNo(header.has_deposit);
  if (key === 'project_days') return daysLabel(header.project_days);
  if (key === 'project_hours') return hoursLabel(header.project_hours);
  if (key === 'vat_enabled') return yesNo(header.vat_enabled);
  if (key === 'vat_pct') return percent(header.vat_pct);
  if (key === 'admin_pct' || key === 'contingency_pct' || key === 'profit_pct') return percent(header[key]);
  if (key === 'admin_cap') return money(header.admin_cap);
  if (key === 'payment_structure_applicable') return yesNo(header.payment_structure_applicable);
  if (key === 'description') return htmlToPlainText(text(header[key]));
  return text(header[key]);
}

function displayOrNull(value: string): string | null {
  const trimmed = String(value || '').trim();
  return trimmed ? trimmed : null;
}

function valuesEqual(a: string | null, b: string | null): boolean {
  return String(a || '') === String(b || '');
}

type VdField = {
  key: string;
  label: string;
  group: SurveyDeltaReviewGroupId;
  value: string;
};

export function extractVisualDesignerFields(line: {
  name?: string;
  qty?: number;
  meta_json?: any;
  metaJson?: any;
} | { fields?: Record<string, string>; name?: string; qty?: number }): VdField[] {
  const stored = (line as { fields?: Record<string, string> }).fields;
  if (stored && Object.keys(stored).length > 0) {
    return Object.entries(stored).map(([key, value]) => {
      const mapped = VD_FIELD_META[key] || { label: key, group: 'specification' as const };
      return { key, label: mapped.label, group: mapped.group, value: String(value || '') };
    });
  }

  const meta = (line as any).meta_json ?? (line as any).metaJson ?? {};
  const specs = meta?.specs && typeof meta.specs === 'object' ? meta.specs : {};
  const options = specs.options && typeof specs.options === 'object' ? specs.options : {};
  const summarySpecs = { ...specs };
  // A non-bay save used to copy the entered width into bay_tangent_width_mm.
  // Use that number for Size when width_mm itself was not stored.
  if (summarySpecs.width_mm == null && !isBayLikePayload(meta?.window_style?.templatePayload) && options.bay_tangent_width_mm != null) {
    summarySpecs.width_mm = options.bay_tangent_width_mm;
  }
  const fields: VdField[] = getDesignerSummaryKeyedRows({ ...meta, specs: summarySpecs }).map((row) => ({
    key: row.key,
    label: row.label,
    group: 'windows_doors' as const,
    value: row.value,
  }));
  const qty = String(asNumber((line as any).qty) ?? '').trim();
  if (qty) {
    const afterType = fields.findIndex((f) => f.key === 'window_type_name') + 1;
    fields.splice(afterType, 0, { key: 'qty', label: 'Quantity', group: 'windows_doors', value: qty });
  }
  return fields;
}

const VD_FIELD_META: Record<string, { label: string; group: SurveyDeltaReviewGroupId }> = {
  window_type_name: { label: 'Window type', group: 'windows_doors' },
  profile_definition: { label: 'Profile Definition', group: 'windows_doors' },
  material_name: { label: 'Material', group: 'windows_doors' },
  bay_tangent_width: { label: 'Tangent width', group: 'windows_doors' },
  height_mm: { label: 'Height', group: 'windows_doors' },
  width_height_size: { label: 'Size', group: 'windows_doors' },
  glass_type: { label: 'Glass', group: 'windows_doors' },
  glass_specification: { label: 'Glass Specification', group: 'windows_doors' },
  variant: { label: 'Profile type', group: 'windows_doors' },
  beading_type: { label: 'Beading', group: 'windows_doors' },
  frame_color_external: { label: 'External colour / finish', group: 'windows_doors' },
  frame_color_internal: { label: 'Internal colour / finish', group: 'windows_doors' },
  frame_color_combined: { label: 'Colour / finish', group: 'windows_doors' },
  handle_color: { label: 'Handle colour', group: 'windows_doors' },
  hinge_placement: { label: 'Hinge', group: 'windows_doors' },
  open_direction: { label: 'Opens', group: 'windows_doors' },
  master_leaf: { label: 'Master leaf', group: 'windows_doors' },
  panel_count: { label: 'Panels', group: 'windows_doors' },
  stack_direction: { label: 'Stack', group: 'windows_doors' },
  traffic_door: { label: 'Traffic door', group: 'windows_doors' },
  sliding_panels: { label: 'Sliding', group: 'windows_doors' },
  threshold: { label: 'Threshold?', group: 'windows_doors' },
  trickle_vents: { label: 'Trickle vents', group: 'windows_doors' },
  cill_option: { label: 'Cill', group: 'windows_doors' },
  extenders_mm: { label: 'Extenders (mm)', group: 'windows_doors' },
  georgian_bars: { label: 'Georgian', group: 'windows_doors' },
  astragal_bars: { label: 'Astragal', group: 'windows_doors' },
  bay_facet_widths: { label: 'Facet widths (mm)', group: 'windows_doors' },
  bay_projection_depth: { label: 'Projection depth', group: 'windows_doors' },
  bay_joint_angles: { label: 'Joint angles (deg)', group: 'windows_doors' },
  window_hinge_type: { label: 'Hinge type', group: 'windows_doors' },
  astragal_bar_side: { label: 'Astragal bar side', group: 'windows_doors' },
  notes: { label: 'Notes', group: 'windows_doors' },
  // Keys stored by snapshots taken before the review used the shared summary.
  window_type: { label: 'Window type', group: 'windows_doors' },
  qty: { label: 'Quantity', group: 'windows_doors' },
  width: { label: 'Width', group: 'windows_doors' },
  tangent_width: { label: 'Tangent width', group: 'windows_doors' },
  height: { label: 'Height', group: 'windows_doors' },
  facet_widths: { label: 'Facet widths', group: 'windows_doors' },
  projection: { label: 'Projection depth', group: 'windows_doors' },
  joint_angles: { label: 'Joint angles', group: 'windows_doors' },
  reference: { label: 'Reference', group: 'windows_doors' },
  glass: { label: 'Glass', group: 'windows_doors' },
  profile: { label: 'Profile type', group: 'windows_doors' },
  bead: { label: 'Bead', group: 'windows_doors' },
  colour: { label: 'Colour / finish', group: 'windows_doors' },
  colour_ext: { label: 'External colour / finish', group: 'windows_doors' },
  colour_int: { label: 'Internal colour / finish', group: 'windows_doors' },
  handle_colour: { label: 'Handle colour', group: 'windows_doors' },
  handle_style: { label: 'Handle style', group: 'windows_doors' },
  hinge: { label: 'Hinge', group: 'windows_doors' },
  opens: { label: 'Opens', group: 'windows_doors' },
  trickle: { label: 'Trickle vents', group: 'windows_doors' },
  cill: { label: 'Cill', group: 'windows_doors' },
  extenders: { label: 'Extenders (mm)', group: 'windows_doors' },
  unit_price: { label: 'Unit price', group: 'quote_details' },
  line_total: { label: 'Line total', group: 'quote_details' },
  qty_commercial: { label: 'Quantity', group: 'quote_details' },
};

const MEASURE_FIELD_KEYS = new Set([
  'width_height_size',
  'bay_tangent_width',
  'height_mm',
  'bay_facet_widths',
  'bay_projection_depth',
  'bay_joint_angles',
  'cill_option',
  'extenders_mm',
  'width',
  'tangent_width',
  'height',
  'facet_widths',
  'projection',
  'joint_angles',
  'cill',
  'extenders',
]);

function fieldValue(fields: VdField[], key: string): string | null {
  return displayOrNull(fields.find((field) => field.key === key)?.value || '');
}

function shouldSkipMeasureDiff(args: {
  key: string;
  original: string | null;
  final: string | null;
  added: boolean;
  firstSameNameFields: VdField[];
}): boolean {
  if (!MEASURE_FIELD_KEYS.has(args.key)) return false;
  if (!args.final) {
    // Empty Final measures mean "not re-surveyed yet", not a deletion.
    return true;
  }
  if (args.added) {
    const inherited = fieldValue(args.firstSameNameFields, args.key);
    // Drawing size carried for visuals should not look like a newly surveyed value.
    if (inherited && valuesEqual(args.final, inherited)) return true;
  }
  return false;
}

function matchByName<T extends { name: string }>(first: T[], final: T[]): Array<{
  name: string;
  first: T | null;
  final: T | null;
}> {
  const remainingFinal = [...final];
  const pairs: Array<{ name: string; first: T | null; final: T | null }> = [];
  first.forEach((item) => {
    const idx = remainingFinal.findIndex((row) => row.name === item.name);
    if (idx >= 0) {
      pairs.push({ name: item.name, first: item, final: remainingFinal[idx] });
      remainingFinal.splice(idx, 1);
    } else {
      pairs.push({ name: item.name, first: item, final: null });
    }
  });
  remainingFinal.forEach((item) => {
    pairs.push({ name: item.name, first: null, final: item });
  });
  return pairs;
}

function rowId(parts: Array<string | null | undefined>): string {
  return parts.map((p) => String(p || '')).join('|');
}

export function buildSurveyDeltaReview(input: {
  firstHeader: Record<string, unknown>;
  finalHeader: Record<string, unknown>;
  firstLines: any[];
  finalLines: any[];
}): SurveyDeltaReviewSection[] {
  const rows: SurveyDeltaReviewRow[] = [];
  const firstHeader = input.firstHeader || {};
  const finalHeader = input.finalHeader || {};

  Object.keys(HEADER_LABELS).forEach((key) => {
    const meta = HEADER_LABELS[key];
    const original = displayOrNull(formatHeaderValue(key, firstHeader));
    const final = displayOrNull(formatHeaderValue(key, finalHeader));
    if (valuesEqual(original, final)) return;
    rows.push({
      id: rowId(['header', key]),
      group: meta.group,
      item: null,
      label: meta.label,
      original,
      final,
    });
  });

  const firstLines = input.firstLines || [];
  const finalLines = input.finalLines || [];
  const isVd = (line: any) => !!(
    line?.isVisualDesigner
    || String(line?.meta_json?.feature || line?.metaJson?.feature || '').trim() === 'visual_designer'
  );

  const firstVd = firstLines.filter(isVd);
  const finalVd = finalLines.filter(isVd);
  matchByName(firstVd.map((l) => ({ ...l, name: String(l.name || 'Window / door') })), finalVd.map((l) => ({ ...l, name: String(l.name || 'Window / door') }))).forEach((pair) => {
    const itemName = pair.name || 'Window / door';
    if (!pair.first || !pair.final) {
      rows.push({
        id: rowId(['vd-item', itemName, pair.first ? 'removed' : 'added']),
        group: 'windows_doors',
        item: itemName,
        label: pair.first ? 'Removed' : 'Added',
        original: pair.first ? itemName : null,
        final: pair.final ? itemName : null,
      });
    }
    const firstFields = pair.first ? extractVisualDesignerFields(pair.first) : [];
    const finalFields = pair.final ? extractVisualDesignerFields(pair.final) : [];
    const firstSameNameFields = pair.first
      ? firstFields
      : extractVisualDesignerFields(firstVd.find((line) => String(line.name || 'Window / door') === itemName) || {});
    const keys = Array.from(new Set([...firstFields.map((f) => f.key), ...finalFields.map((f) => f.key)]));
    keys.forEach((key) => {
      const a = firstFields.find((f) => f.key === key);
      const b = finalFields.find((f) => f.key === key);
      const original = displayOrNull(a?.value || '');
      const final = displayOrNull(b?.value || '');
      if (valuesEqual(original, final)) return;
      if (shouldSkipMeasureDiff({
        key,
        original,
        final,
        added: !pair.first && !!pair.final,
        firstSameNameFields,
      })) return;
      const meta = a || b;
      rows.push({
        id: rowId(['vd', itemName, key]),
        group: meta?.group || 'specification',
        item: itemName,
        label: meta?.label || key,
        original,
        final,
      });
    });
  });

  const firstCommercial = firstLines.filter((l) => !isVd(l));
  const finalCommercial = finalLines.filter((l) => !isVd(l));
  matchByName(
    firstCommercial.map((l) => ({ ...l, name: String(l.name || 'Item') })),
    finalCommercial.map((l) => ({ ...l, name: String(l.name || 'Item') })),
  ).forEach((pair) => {
    const itemName = pair.name || 'Item';
    if (!pair.first || !pair.final) {
      rows.push({
        id: rowId(['item', itemName, pair.first ? 'removed' : 'added']),
        group: 'quote_details',
        item: itemName,
        label: pair.first ? 'Removed' : 'Added',
        original: pair.first ? itemName : null,
        final: pair.final ? itemName : null,
      });
    }
    const qtyA = pair.first ? String(asNumber(pair.first.qty ?? pair.first.qty) ?? '') : '';
    const qtyB = pair.final ? String(asNumber(pair.final.qty ?? pair.final.qty) ?? '') : '';
    if (!valuesEqual(displayOrNull(qtyA), displayOrNull(qtyB))) {
      rows.push({
        id: rowId(['item', itemName, 'qty']),
        group: 'quote_details',
        item: itemName,
        label: 'Quantity',
        original: displayOrNull(qtyA),
        final: displayOrNull(qtyB),
      });
    }
    const priceA = pair.first ? money(pair.first.unit_price ?? pair.first.unitPrice) : '';
    const priceB = pair.final ? money(pair.final.unit_price ?? pair.final.unitPrice) : '';
    if (!valuesEqual(displayOrNull(priceA), displayOrNull(priceB))) {
      rows.push({
        id: rowId(['item', itemName, 'price']),
        group: 'quote_details',
        item: itemName,
        label: 'Unit price',
        original: displayOrNull(priceA),
        final: displayOrNull(priceB),
      });
    }
  });

  const order: SurveyDeltaReviewGroupId[] = [
    'quote_details',
    'windows_doors',
    'dimensions_design',
    'specification',
  ];
  return order
    .map((id) => ({
      id,
      title: GROUP_TITLES[id],
      rows: rows.filter((row) => row.group === id),
    }))
    .filter((section) => section.rows.length > 0);
}

export function reviewSectionsFromSnapshot(snapshot: {
  first?: { header?: Record<string, unknown>; lines?: any[] };
  final?: { header?: Record<string, unknown>; lines?: any[] };
  changes?: { review?: SurveyDeltaReviewSection[] };
} | null | undefined): SurveyDeltaReviewSection[] {
  if (!snapshot) return [];
  const storedReview = snapshot.changes?.review;
  if (Array.isArray(storedReview) && storedReview.length > 0) {
    return storedReview;
  }
  return buildSurveyDeltaReview({
    firstHeader: snapshot.first?.header || {},
    finalHeader: snapshot.final?.header || {},
    firstLines: snapshot.first?.lines || [],
    finalLines: snapshot.final?.lines || [],
  });
}

export function flattenVisualDesignerFieldsForSnapshot(line: any): Record<string, string> {
  const fields: Record<string, string> = {};
  extractVisualDesignerFields(line).forEach((field) => {
    fields[field.key] = field.value;
  });
  return fields;
}

export function groupReviewRowsByItem<T extends { item?: string | null }>(rows: T[]): Array<{
  item: string | null;
  rows: T[];
}> {
  const groups: Array<{ item: string | null; rows: T[] }> = [];
  rows.forEach((row) => {
    const item = row.item ? String(row.item) : null;
    const last = groups[groups.length - 1];
    if (last && last.item === item) {
      last.rows.push(row);
      return;
    }
    groups.push({ item, rows: [row] });
  });
  return groups;
}
